import { useMemo, useState } from 'react';
import * as yup from 'yup';
import useApiFetch from '@/hooks/useApiFetch';
import candidatesServices from '@/services/candidates';
import { FetchApiIds } from '@/types';
import { InterviewType, PersonType } from '@/types/api';
import { InterviewTypes } from '@/utils/interviews';
import InterviewBoxComponent, { InterviewBoxType } from './InterviewBox.component';

export type InterviewBoxProps = Pick<
  InterviewBoxType,
  'children' | 'isCollapsible' | 'isOpen' | 'onToggleOpen' | 'autoScroll' | 'onAutoScroll'
> & {
  interview: InterviewType;
  candidate: PersonType;
  isSubmitButtonDisabled?: boolean;
  isAlreadySent?: boolean;
  hideCandidateDetails?: boolean;
  hideSubmitButton?: boolean;
  onCreateInterview: (interviewId: string, onCreateSuccess?: () => void) => void;
  onSubmitInterview: (interviewId: string, type: InterviewTypes) => void;
  selectInterviewToDelete: () => void;
  onEditInterviewName: () => void;
  onChangeEmailAddress: (email: string) => void;
  onRefetchInterview: () => void;
};

const emailSchema = yup.string().email('Invalid e-mail address').required();

const INTERVIEW_LINK_PREFIX = {
  [InterviewTypes.PRE_SCREENING]: 'interview/pre-screening',
  [InterviewTypes.TECHNICAL_CHALLENGE]: 'interview/technical-challenge',
};

export default function InterviewBox({
  interview,
  candidate: defaultCandidate,
  isSubmitButtonDisabled = true,
  isAlreadySent = false,
  hideCandidateDetails = false,
  hideSubmitButton = false,
  onSubmitInterview,
  onCreateInterview,
  onEditInterviewName,
  selectInterviewToDelete,
  onChangeEmailAddress,
  onRefetchInterview,
  ...props
}: InterviewBoxProps): JSX.Element {
  const [candidate, setCandidate] = useState({
    isEditingEmail: !defaultCandidate.email,
    name: `${defaultCandidate.firstName} ${defaultCandidate.lastName}`,
    email: defaultCandidate.email ?? '',
    draft: '',
    isEmailValid: !!defaultCandidate.email,
    id: defaultCandidate.id,
  });

  const onEditCandidateEmail = (): void => {
    setCandidate((prev) => ({
      ...prev,
      draft: prev.email,
      isEditingEmail: true,
    }));
  };

  const onCancelEditingEmail = (): void => {
    setCandidate((prev) => ({
      ...prev,
      email: prev.draft,
      draft: '',
      isEditingEmail: false,
    }));
  };

  const onChangeCandidateEmail = async (event: any): Promise<void> => {
    const {
      target: { value },
    } = event;
    let isEmailValid;
    try {
      await emailSchema.validate(value);
      isEmailValid = true;
    } catch {
      isEmailValid = false;
    }

    setCandidate((prev) => ({
      ...prev,
      email: value,
      isEmailValid,
    }));
  };

  const onSaveCandidateEmail = (): void => {
    onChangeEmailAddress(candidate.email);
    setCandidate((prev) => ({
      ...prev,
      isEditingEmail: false,
    }));
  };

  const interviewLink = useMemo(() => {
    const interviewId = interview.id;
    const candidateId = candidate.id;

    const url = window.location.origin;

    const interviewUrl = `${url}/${
      INTERVIEW_LINK_PREFIX[interview.type]
    }/${interviewId}/${candidateId}`;

    return interviewUrl;
  }, [candidate.id, interview.id, interview.type]);

  const allTechStacksResponse = useApiFetch(
    [FetchApiIds.ALL_TECHS],
    candidatesServices.getAllTechsStacks
  );

  const isUUID = (str: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  const resolvedSkillName = useMemo(() => {
    const interviewTechnicalChallenge = interview.technicalChallenge;

    if (interviewTechnicalChallenge?.skill?.name) {
      return interviewTechnicalChallenge.skill.name;
    }

    if (interviewTechnicalChallenge?.skillId && allTechStacksResponse?.data) {
      const skill = allTechStacksResponse.data.find(
        (tech) => tech.id === interviewTechnicalChallenge.skillId
      );
      return skill?.name;
    }

    if (interviewTechnicalChallenge?.languageUsedByCandidate) {
      const languageValue = interviewTechnicalChallenge.languageUsedByCandidate;

      if (isUUID(languageValue) && allTechStacksResponse?.data) {
        const skill = allTechStacksResponse.data.find((tech) => tech.id === languageValue);
        return skill?.name;
      }
      return languageValue;
    }

    return undefined;
  }, [interview.technicalChallenge, allTechStacksResponse?.data]);

  return (
    <InterviewBoxComponent
      interviewLink={interviewLink}
      technicalChallengeName={interview.technicalChallenge?.technicalChallenge.name}
      type={interview.type}
      id={interview.id}
      candidate={candidate}
      interviewName={interview.title}
      onSaveCandidateEmail={onSaveCandidateEmail}
      onEditCandidateEmail={onEditCandidateEmail}
      onDeleteInterview={selectInterviewToDelete}
      onCancelEditingEmail={onCancelEditingEmail}
      onEditInterview={onEditInterviewName}
      onChangeCandidateEmail={onChangeCandidateEmail}
      isSubmitButtonDisabled={isSubmitButtonDisabled}
      isAlreadySent={isAlreadySent}
      hideCandidateDetails={hideCandidateDetails}
      hideSubmitButton={hideSubmitButton}
      onSubmitInterview={onSubmitInterview}
      isSent={interview.isAlreadySent}
      technicalChallengeId={interview.technicalChallenge?.id}
      onRefetchInterview={onRefetchInterview}
      onCreateInterview={onCreateInterview}
      skillName={resolvedSkillName}
      technologyUsedInChallenge={interview.technicalChallenge?.languageUsedByCandidate}
      {...props}
    />
  );
}
